import React, { useState } from "react";
import api from "@/lib/apiClient";
import { useViewMode } from "@/lib/ViewModeContext";
import { useAuth } from "@/lib/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import Base44ConfigDialog from "@/components/settings/Base44ConfigDialog";
import NetworkAccessCard from "@/components/settings/NetworkAccessCard";
import { CheckCircle, Flask, Broadcast, Info, PencilSimple } from "@phosphor-icons/react";

export default function Settings() {
  const { user } = useAuth();
  const { viewMode, setViewMode, globalMode, globalMeta, loaded } = useViewMode() || {};
  const [editingMode, setEditingMode] = useState(null);
  const [metaOverride, setMetaOverride] = useState(null);

  if (!loaded) return <div className="text-zinc-500">Loading…</div>;

  const isAdmin = user?.role === "admin";
  const isTest = viewMode === "test";
  const liveCfg = (metaOverride || globalMeta)?.base44_live || {};
  const testCfg = (metaOverride || globalMeta)?.base44_test || {};

  const refreshMeta = async () => {
    try {
      const { data } = await api.get("/system/mode");
      setMetaOverride(data);
    } catch (err) {
      // Non-fatal: keep prior meta in state so the page doesn't blank out.
      console.warn("Settings: failed to refresh system mode meta", err);
    }
  };

  return (
    <div className="space-y-6 max-w-3xl" data-testid="settings-page">
      <div>
        <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-semibold">Admin</div>
        <h1 className="font-heading font-black text-3xl sm:text-4xl mt-1">Settings</h1>
        <p className="text-sm text-zinc-600 mt-1">
          Your personal view mode only affects what <em>you</em> see in this browser. The rest of
          your team — and the mobile app — keep using the global mode below.
        </p>
      </div>

      <Card
        className={`p-6 border-2 ${isTest ? "border-yellow-400 bg-yellow-50/50" : "border-zinc-200"}`}
        data-testid="my-mode-card"
      >
        <div className="flex items-start gap-4">
          <div
            className="w-12 h-12 rounded-md flex items-center justify-center shrink-0"
            style={{ background: isTest ? "#FEF3C7" : "#D1FAE5", color: isTest ? "#92400E" : "#047857" }}
          >
            {isTest ? <Flask size={26} weight="fill" /> : <Broadcast size={26} weight="fill" />}
          </div>
          <div className="flex-1">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-semibold">Your view</div>
                <h3 className="font-heading font-bold text-lg">
                  {isTest ? "Test mode" : "Live mode"}
                  <span className="text-sm font-normal text-zinc-500 ml-2">· just for you</span>
                </h3>
                <p className="text-sm text-zinc-600 mt-1">
                  {isTest
                    ? "You're viewing sandbox data. Scans you do here only affect the test database. Your team continues to see live data."
                    : "You're viewing live data, same as the rest of the team."}
                </p>
                <p className="text-xs text-zinc-500 mt-2">
                  Signed in as <span className="font-semibold">{user?.name}</span> · preference is
                  saved per-account in this browser.
                </p>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-xs uppercase tracking-widest font-semibold">Test</span>
                <Switch
                  checked={isTest}
                  onCheckedChange={(on) => setViewMode(on ? "test" : "live")}
                  data-testid="my-mode-toggle"
                />
              </div>
            </div>
          </div>
        </div>
      </Card>

      <Card className="p-5 border-zinc-200 bg-zinc-50" data-testid="global-mode-card">
        <div className="flex items-start gap-3">
          <Info size={20} weight="bold" className="text-zinc-500 shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-semibold">Global default (read-only)</div>
            <div className="text-sm text-zinc-700 mt-0.5">
              The team-wide default is set to{" "}
              <span className="font-semibold uppercase tracking-widest text-xs">{globalMode}</span>.
              {viewMode !== globalMode && (
                <span className="text-yellow-800 ml-1">
                  Your view ({viewMode}) overrides this for your session.
                </span>
              )}
            </div>
            {globalMeta?.last_changed_at && (
              <div className="text-xs text-zinc-500 mt-1">
                Last global change {new Date(globalMeta.last_changed_at).toLocaleString()}
                {globalMeta.last_changed_by_email && ` · ${globalMeta.last_changed_by_email}`}
              </div>
            )}
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Base44Card
          title="Live Base44"
          mode="live"
          cfg={liveCfg}
          isAdmin={isAdmin}
          onEdit={() => setEditingMode("live")}
          testId="base44-live-card"
        />
        <Base44Card
          title="Test Base44"
          mode="test"
          cfg={testCfg}
          isAdmin={isAdmin}
          onEdit={() => setEditingMode("test")}
          testId="base44-test-card"
        />
      </div>

      <Card className="p-6 border-zinc-200">
        <h3 className="font-heading font-bold text-lg">About this connection</h3>
        <p className="text-sm text-zinc-600 mt-2 leading-relaxed">
          This web app is a desktop companion connected to your mobile app&apos;s deployed backend at{" "}
          <span className="font-mono text-zinc-900">picking-building-qa.emergent.host</span>. The
          per-account view mode is delivered via an <span className="font-mono">X-CD-Mode</span>{" "}
          header on each request, so toggling it never affects other users.
        </p>
      </Card>

      <NetworkAccessCard />

      <Base44ConfigDialog
        open={!!editingMode}
        mode={editingMode}
        cfg={editingMode === "test" ? testCfg : liveCfg}
        onClose={() => setEditingMode(null)}
        onSaved={async () => { await refreshMeta(); setEditingMode(null); }}
      />
    </div>
  );
}

function Base44Card({ title, mode, cfg, isAdmin, onEdit, testId }) {
  const configured = cfg?.configured || cfg?.has_api_key;
  return (
    <Card className="p-5 border-zinc-200" data-testid={testId}>
      <div className="flex items-center justify-between mb-3">
        <h4 className="font-heading font-bold">{title}</h4>
        <div className="flex items-center gap-2">
          <span
            className="text-[10px] uppercase tracking-[0.2em] font-bold rounded-md px-2 py-1"
            style={
              configured
                ? { background: "#D1FAE5", color: "#047857" }
                : { background: "#FEE2E2", color: "#991B1B" }
            }
          >
            {configured ? "Configured" : "Missing"}
          </span>
          {isAdmin && (
            <Button
              variant="outline"
              size="sm"
              onClick={onEdit}
              className="h-7 px-2 text-xs"
              data-testid={`${testId}-edit`}
            >
              <PencilSimple size={12} weight="bold" className="mr-1" />
              {configured ? "Edit" : "Add"}
            </Button>
          )}
        </div>
      </div>
      <div className="space-y-2 text-sm">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-semibold">URL</div>
          <div className="font-mono text-xs break-all">{cfg?.url || "—"}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-semibold">App ID</div>
          <div className="font-mono text-xs break-all">{cfg?.app_id || "—"}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-semibold">API key</div>
          <div className="font-mono text-xs">{cfg?.api_key_masked || "—"}</div>
        </div>
      </div>
    </Card>
  );
}
